/**
 * Teen Journey History Page
 * Shows all past completed 21-day journeys with stats and reflections.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Trophy,
  Zap,
  Flame,
  Calendar,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Star
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeenJourneyHistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJourney, setExpandedJourney] = useState(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/challenge/history`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error("Error loading history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const toggleExpand = (index) => {
    setExpandedJourney(expandedJourney === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        >
          <Trophy size={40} className="text-amber-500" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
      {/* Header */}
      <div className="p-4">
        <button
          onClick={() => navigate("/teen/challenge")}
          className="text-white/60 hover:text-white flex items-center gap-2"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      {/* Title */}
      <div className="px-6 pt-2 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Trophy size={20} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Journey History</h1>
        </div>
        <p className="text-white/60">
          {history.length} completed journey{history.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* History List */}
      {history.length > 0 ? (
        <div className="px-6 pb-8 space-y-4">
          {history.map((journey, index) => {
            const isExpanded = expandedJourney === index;
            const reflectionCount = Object.keys(journey.reflections || {}).length;
            
            return (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-2xl overflow-hidden"
              >
                {/* Journey Card */}
                <button
                  onClick={() => toggleExpand(index)}
                  className="w-full p-5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                        <Trophy size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">
                          Journey #{history.length - index}
                        </h3>
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                          <Calendar size={14} />
                          <span>Completed {formatDate(journey.completed_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-amber-400">
                          <Zap size={16} />
                          <span className="font-bold">{journey.total_xp || 0}</span>
                        </div>
                        <span className="text-white/50 text-xs">XP</span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-white/50" />
                      ) : (
                        <ChevronDown size={20} className="text-white/50" />
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex gap-6 mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <Flame size={16} className="text-orange-400" />
                      <span className="text-white/70 text-sm">
                        {journey.streak || 21} day streak
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-blue-400" />
                      <span className="text-white/70 text-sm">
                        {reflectionCount} reflections
                      </span>
                    </div>
                  </div>
                </button>

                {/* Expanded Reflections */}
                <AnimatePresence>
                  {isExpanded && journey.reflections && Object.keys(journey.reflections).length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-white/5 border-t border-white/10 space-y-3">
                        <h4 className="text-white/80 font-semibold text-sm flex items-center gap-2">
                          <Star size={14} className="text-purple-400" />
                          Your Reflections
                        </h4>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {Object.entries(journey.reflections)
                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                            .map(([day, reflection]) => (
                              <div 
                                key={day}
                                className="p-3 rounded-xl bg-white/5 border border-white/10"
                              >
                                <div className="text-purple-400 text-xs font-semibold mb-1">
                                  Day {day}
                                </div>
                                <p className="text-white/70 text-sm italic">
                                  "{reflection}"
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Total Stats Summary */}
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-500/30">
            <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <Star size={20} className="text-purple-400" />
              All-Time Stats
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400">
                  {history.length}
                </div>
                <div className="text-white/50 text-xs">Journeys</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-400">
                  {history.reduce((sum, j) => sum + (j.total_xp || 0), 0)}
                </div>
                <div className="text-white/50 text-xs">Total XP</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">
                  {history.length * 21}
                </div>
                <div className="text-white/50 text-xs">Days Complete</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-6 pb-8">
          <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <Trophy size={32} className="text-amber-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">No Completed Journeys Yet</h3>
            <p className="text-white/50 text-sm mb-4">
              Complete your first 21-day journey to see it here!
            </p>
            <button
              onClick={() => navigate("/teen/challenge")}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
            >
              Continue Challenge
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeenJourneyHistoryPage;
